import React, { useState, useEffect, useMemo, Dispatch, SetStateAction } from 'react';
import {
  Button,
  Dropdown,
  Option,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Spinner,
  makeStyles,
  tokens,
  Field,
  Label,
  Card,
  CardHeader,
  Title3,
  CardPreview,
} from '@fluentui/react-components';
import {
  ChevronUpRegular,
  ChevronDownRegular,
} from '@fluentui/react-icons';
import {
  Tender,
  TenderingPart,
  Tendering,
  RankingType,
} from '../../../models/tendering';

import { deepCloneObject } from '../../../utils/clone';

import {
  setTenderingPartRankingByPrice,
  setTenderingPartRankingByRankingValue,
  setTenderListRankings,
  sortByRank,
} from '../../../utils/ranking';



interface TenderingAdminRankingProps {
  tenderingDocument: Tendering,
  tenders: Tender[],
  updateTenderingData: Dispatch<SetStateAction<Tendering>>;
}

export const TenderingRanking: React.FC<TenderingAdminRankingProps> = ({
  tenderingDocument,
  tenders,
  updateTenderingData
}) => {
  const [selectedPart, setSelectedPart] = useState<TenderingPart | null>(null);
  const [tenderList, setTenderList] = useState<Tender[]>([]);

  const tenderProperties = ["content.tenderAnswers", "content.tenderingParts"];

  const getEvaluatedParts = (): TenderingPart[] => {
    return tenderingDocument.tenderingParts.filter(p => p.evaluated);
  };

  const getInitPrice = (tender: Tender): number => {
    if (!selectedPart) return 0;
    const tenderingPart = tender.tenderingParts.find(tp => tp.tenderingPartID === selectedPart.tenderingPartID);
    return tenderingPart?.priceInit || 0;
  };

  const getExtPrice = (tender: Tender): number => {
    if (!selectedPart) return 0;
    const tenderingPart = tender.tenderingParts.find(tp => tp.tenderingPartID === selectedPart.tenderingPartID);
    return tenderingPart?.priceExt || 0;
  };

  const onPartChange = (partId: string) => {
    const part = getEvaluatedParts().find(p => p.tenderingPartID.toString() === partId);
    if (!part) return;

    setSelectedPart(part);
    let updatedTenderList: Tender[] = deepCloneObject(tenders);

    updatedTenderList.forEach(tender => {
      let price: any;
      
      // If double price
      if (typeof part.ranking === 'object' && part.ranking !== null) {
        const price1 = tender.tenderAnswers?.find(a => a.questionID === part.ranking.prices[0]);
        const price2 = tender.tenderAnswers?.find(a => a.questionID === part.ranking.prices[1]);
        if (price1 && price2) {
          price = {
            priceInit: price1.value,
            priceExt: price2.value,
            value: 0
          };
          price.value = (Number(price1.value) * 12) + (Number(price2.value) * 24);
        }
      } else {
        price = tender.tenderAnswers?.find(a => a.questionID === part.ranking);
      }

      if (price) {
        tender.tenderingParts.forEach(tpart => {
          if (part.tenderingPartID === tpart.tenderingPartID) {
            tpart.price = Number(price.value);
            tpart.rankingValue = Number(price.rankingValue);
            if (price.priceInit && price.priceExt) {
              tpart.priceInit = Number(price.priceInit);
              tpart.priceExt = Number(price.priceExt);
            }
          }
        });
      }
    });

    updatedTenderList = updatedTenderList.filter(tl => {
      let found = false;
      for (const tp of tl.tenderingParts) {
        if (tp.price > 0 && tp.tenderingPartID === part.tenderingPartID) {
          found = true;
          tl.price = Number(tp.price);
          tl.rankingValue = Number(tp.rankingValue);
          break;
        }
      }
      return found;
    });

    if (part.rankings) {
      updatedTenderList = setTenderListRankings(updatedTenderList, part.rankings);
      updatedTenderList = sortByRank(updatedTenderList);
    }

    setTenderList(updatedTenderList);
  };

  const rankByPrice = () => {
    if (!selectedPart) return;
    
    selectedPart.rankings = setTenderingPartRankingByPrice(tenderList);
    selectedPart.rankingType = RankingType.Price;
    onPartChange(selectedPart.tenderingPartID.toString());
  };

  const rankByPreIndexingPrice = () => {
    if (!selectedPart) return;
    
    selectedPart.rankings = setTenderingPartRankingByRankingValue(tenderList);
    selectedPart.rankingType = RankingType.RankingValue;
    onPartChange(selectedPart.tenderingPartID.toString());
  };

  const moveDown = (tender: Tender) => {
    if (!selectedPart?.rankings) return;
    
    const target = selectedPart.rankings.find((r: any) => r.tenderID === tender.tenderID);
    const dest = selectedPart.rankings.find((r: any) => r.rank === target!.rank + 1);
    
    if (target && dest) {
      target.rank++;
      dest.rank--;
      onPartChange(selectedPart.tenderingPartID.toString());
    }
  };

  const moveUp = (tender: Tender) => {
    if (!selectedPart?.rankings) return;
    
    const target = selectedPart.rankings.find((r: any) => r.tenderID === tender.tenderID);
    const dest = selectedPart.rankings.find((r: any) => r.rank === target!.rank - 1);
    
    if (target && dest) {
      target.rank--;
      dest.rank++;
      onPartChange(selectedPart.tenderingPartID.toString());
    }
  };

  const getDisplayedColumns = () => {
    if (!selectedPart) return [];
    
    let columns = ['tenderName'];
    
    if (selectedPart.rankingType === RankingType.RankingValue) {
      columns.push('rankingValue');
    }
    
    if (typeof selectedPart.ranking === 'object' && selectedPart.ranking !== null) {
      columns.push('priceInit', 'priceExt');
    } else {
      columns.push('price');
    }
    
    columns.push('rank', 'action');
    return columns;
  };

  const evaluatedParts = useMemo(() => getEvaluatedParts(), [tenderingDocument.tenderingParts]);
  const displayedColumns = useMemo(() => getDisplayedColumns(), [selectedPart]);

  return (
    <Card>
      <CardPreview>
        <Title3>Rangordningar</Title3>
      </CardPreview>
      <div className="overflow-auto p-4">
        <div className="mb-6 flex flex-col gap-4">
          <Field>
            <Label>Välj delområde</Label>
            <Dropdown
              placeholder="Välj Delområde"
              onOptionSelect={(_, data) => onPartChange(data.optionValue || '')}
              style={{ minWidth: '25%', maxWidth: '100%' }}
              >
              {evaluatedParts.map((part) => (
                <Option key={part.tenderingPartID} value={part.tenderingPartID.toString()}>
                  {part.tenderingPart}
                </Option>
              ))}
            </Dropdown>
          </Field>
          
          {selectedPart && (
            <div className="flex gap-4 mt-4">
              <Button appearance="primary" onClick={rankByPrice}>
                Rangordna på pris
              </Button>
              <Button appearance="primary" onClick={rankByPreIndexingPrice}>
                Rangordna på orginal pris
              </Button>
            </div>
          )}
        </div>
  
        {selectedPart && tenderList.length > 0 && (
          <Table className="w-full" aria-label="Tendering ranking table">
            <TableHeader>
              <TableRow>
                {displayedColumns.includes('tenderName') && (
                  <TableHeaderCell>Verksamhet</TableHeaderCell>
                )}
                {displayedColumns.includes('rankingValue') && (
                  <TableHeaderCell>Orginal Pris</TableHeaderCell>
                )}
                {displayedColumns.includes('price') && (
                  <TableHeaderCell>Pris</TableHeaderCell>
                )}
                {displayedColumns.includes('priceInit') && (
                  <TableHeaderCell>Initialt Pris</TableHeaderCell>
                )}
                {displayedColumns.includes('priceExt') && (
                  <TableHeaderCell>Förlängningspris</TableHeaderCell>
                )}
                {displayedColumns.includes('rank') && (
                  <TableHeaderCell>Rangordning</TableHeaderCell>
                )}
                {displayedColumns.includes('action') && (
                  <TableHeaderCell></TableHeaderCell>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenderList.map((tender) => (
                <TableRow key={tender.tenderID}>
                  {displayedColumns.includes('tenderName') && (
                    <TableCell>
                      <TableCellLayout>{tender.tenderName}</TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('rankingValue') && (
                    <TableCell>
                      <TableCellLayout>{tender.rankingValue}</TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('price') && (
                    <TableCell>
                      <TableCellLayout>{tender.price}</TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('priceInit') && (
                    <TableCell>
                      <TableCellLayout>
                        {getInitPrice(tender)} <em>SEK</em>
                      </TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('priceExt') && (
                    <TableCell>
                      <TableCellLayout>
                        {getExtPrice(tender)} <em>SEK</em>
                      </TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('rank') && (
                    <TableCell>
                      <TableCellLayout>{tender.rank}</TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('action') && (
                    <TableCell>
                      <TableCellLayout>
                        <div>
                          <ChevronUpRegular
                            onClick={() => moveUp(tender)}
                            title="Flytta Upp"
                            />
                          <ChevronDownRegular
                            onClick={() => moveDown(tender)}
                            title="Flytta Ner"
                            />
                        </div>
                      </TableCellLayout>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </Card>
  );
};

export default TenderingRanking;
